import { type HTMLAttributes } from 'react'

/**
 * Small status pill.
 *
 * The colour scheme matches the V1 visual language:
 *   - `info`     (bleu)    — planning planifié
 *   - `success`  (vert)    — bon rempli
 *   - `warning`  (orange)  — attention requise
 *   - `danger`   (rouge)   — erreur / refus
 *   - `template` (violet)  — template / variant — replaces the V1
 *                            "BDN" string-matching with a proper flag
 *   - `neutral`  (gris)    — info brute
 *
 * The colour helper-text is in French throughout — drivers don't
 * see the variant key.
 */

type Tone = 'info' | 'success' | 'warning' | 'danger' | 'template' | 'neutral'

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone
}

const TONE_CLASSES: Record<Tone, string> = {
  info: 'bg-info-soft text-info',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  template: 'bg-template-soft text-template',
  neutral: 'bg-surface-muted text-text-muted',
}

export function Badge({ tone = 'neutral', className, children, ...rest }: BadgeProps) {
  return (
    <span
      className={[
        'inline-flex items-center gap-1',
        'px-2.5 py-1 rounded-full text-xs font-medium',
        TONE_CLASSES[tone],
        className ?? '',
      ].join(' ')}
      {...rest}
    >
      {children}
    </span>
  )
}
